/**
 * BlindFavorabilityService
 *
 * Pure function: no side effects, no async, no storage calls.
 * Determines wind favorability for each blind/stand location given
 * the current wind cardinal direction.
 *
 * ADR-023: Moon phase is never included in favorability computation.
 */

import { isWindDirectionFavorable } from '@/utils/wind-utils';
import type { Waypoint, BlindMetadata } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BlindFavorabilityResult {
  /** Waypoint (blind/stand) ID */
  locationId: string;
  /** Display name of the stand/blind */
  name: string;
  /** Whether current wind direction is in the ideal directions list */
  favorable: boolean;
  /** The current wind cardinal direction (e.g. 'NW') */
  currentWind: string;
  /** The ideal wind directions configured for this blind */
  idealWindDirections: string[];
}

// ---------------------------------------------------------------------------
// Service function
// ---------------------------------------------------------------------------

/**
 * Compute wind favorability for all blinds/stands in the given waypoint list.
 *
 * Only waypoints of type `Blind` with at least one `idealWindDirections` entry
 * are included — all others are filtered out silently.
 *
 * @param waypoints - All waypoints for the property (filtered internally)
 * @param windCardinal - Current wind direction as a cardinal string (e.g. 'NW', 'S')
 * @returns Array of favorability results, one per qualifying blind/stand
 */
export function computeBlindFavorability(
  waypoints: Waypoint[],
  windCardinal: string,
): BlindFavorabilityResult[] {
  return waypoints
    .filter((wp): wp is Waypoint & { metadata: BlindMetadata } => {
      if (wp.type !== WaypointType.Blind) return false;
      const meta = wp.metadata as BlindMetadata | undefined;
      return (meta?.idealWindDirections?.length ?? 0) > 0;
    })
    .map((wp) => {
      const meta = wp.metadata as BlindMetadata;
      const idealDirs = meta.idealWindDirections ?? [];
      const favorable = isWindDirectionFavorable(
        cardinalToDeg(windCardinal),
        idealDirs,
      );

      return {
        locationId: wp.id,
        name: wp.name,
        favorable,
        currentWind: windCardinal,
        idealWindDirections: idealDirs,
      };
    });
}

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

const CARDINAL_TO_DEG: Record<string, number> = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
  E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
  W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

function cardinalToDeg(cardinal: string): number {
  return CARDINAL_TO_DEG[cardinal] ?? 0;
}
